import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { segmentsQueries } from '@/api/segments/segmentsQueries'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import type { ImportPriceSettingsDto } from '../../types/stockImportsPrices.types'
import {
  collectColumnEntries,
  getConflictingColumnKeys,
} from '../lib/configColumnConflicts'
import type { ConfigFormInput } from '../lib/configFormModel'
import {
  buildPriceFormValues,
  buildPriceSettings,
  type ConfigPriceFormValues,
} from '../lib/configPriceModel'
import { configPriceSchema } from '../lib/configPriceValidation'
import {
  assignSegmentColumn,
  hasPriceSourceColumn,
} from '../lib/configSegmentColumns'
import type { PriceFieldContext } from '../PricesStep/PriceFields'
import type { PriceColumnRole } from '../UrlExcelMapper/UrlExcelMapper.types'

type UseConfigPriceFormParams = {
  settings: ImportPriceSettingsDto | undefined
  canEditPrices: boolean
  isOrganisationSelected: boolean
}

export const useConfigPriceForm = ({
  settings,
  canEditPrices,
  isOrganisationSelected,
}: UseConfigPriceFormParams) => {
  const segmentsQuery = useQuery({
    ...segmentsQueries.list('mine'),
    enabled: canEditPrices,
  })
  const segments = useMemo(
    () => (segmentsQuery.data ?? []).filter((segment) => segment.isActive),
    [segmentsQuery.data]
  )
  const values = useMemo(
    () => buildPriceFormValues(settings, segments),
    [settings, segments]
  )
  const form = useForm<ConfigPriceFormValues>({
    values,
    resolver: zodResolver(configPriceSchema),
    mode: 'onTouched',
    shouldFocusError: false,
  })

  const enabled = useWatch({ control: form.control, name: 'enabled' })
  const context: PriceFieldContext = {
    control: form.control,
    register: form.register,
    errors: form.formState.errors,
    isOrganisationSelected,
  }

  const setColumn = (field: PriceColumnRole, value: string) =>
    form.setValue(field, value, { shouldDirty: true, shouldValidate: true })

  const setSegmentColumn = (columnIndex: number, segmentIds: string[]) =>
    form.setValue(
      'segments',
      assignSegmentColumn(
        form.getValues('segments'),
        String(columnIndex + 1),
        segmentIds
      ),
      { shouldDirty: true, shouldValidate: form.formState.isSubmitted }
    )

  const hasPriceSource = () => hasPriceSourceColumn(form.getValues())

  const hasColumnConflicts = (stock: ConfigFormInput) =>
    getConflictingColumnKeys(collectColumnEntries(stock, form.getValues()))
      .size > 0

  const buildSettings = async (): Promise<
    ImportPriceSettingsDto | null | undefined
  > => {
    if (!canEditPrices) return undefined
    if (!(await form.trigger())) {
      handleFormSubmitError(false)
      return null
    }
    return buildPriceSettings(form.getValues())
  }

  const markClean = () => {
    if (canEditPrices) form.reset(form.getValues())
  }

  return {
    form,
    context,
    columns: {
      context,
      canEdit: canEditPrices,
      visible: canEditPrices && enabled,
      setSegmentColumn,
    },
    hasPriceSource,
    hasColumnConflicts,
    setColumn,
    segmentsQuery,
    canEditPrices,
    isDirty: canEditPrices && form.formState.isDirty,
    buildSettings,
    markClean,
  }
}

export type ConfigPriceFormApi = ReturnType<typeof useConfigPriceForm>
